/**
 * Chapter-wise Prelims Practice
 * Pick subject → chapter → 20-MCQ session (16 static + 4 CA)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios from "@/utils/managedAxios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BookOpen,
  Newspaper,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowLeft,
  Sparkles,
  Trophy,
  Target,
  Search,
  X as XIcon,
  Landmark,
  Scroll,
  Globe2,
  TrendingUp,
  Leaf,
  FlaskConical,
  Network,
  Users,
  ArrowRight,
  Flame,
} from "lucide-react";
import { API_BASE } from "@/App";
import { Input } from "@/components/ui/input";
const SUBJECTS = [
  { key: "polity",                  label: "Polity",       icon: Landmark,      accent: "indigo",  hex: "#6366f1" },
  { key: "history",                 label: "History",      icon: Scroll,        accent: "amber",   hex: "#d97706" },
  { key: "geography",               label: "Geography",    icon: Globe2,        accent: "sky",     hex: "#0284c7" },
  { key: "economy",                 label: "Economy",      icon: TrendingUp,    accent: "blue",    hex: "#2563eb" },
  { key: "environment",             label: "Environment",  icon: Leaf,          accent: "emerald", hex: "#059669" },
  { key: "scitech",                 label: "Sci & Tech",   icon: FlaskConical,  accent: "violet",  hex: "#7c3aed" },
  { key: "international_relations", label: "Int. Relations", icon: Network,     accent: "rose",    hex: "#e11d48" },
  { key: "society_security",        label: "Society/Security", icon: Users,     accent: "teal",    hex: "#0d9488" },
];
const SUBJECT_MAP = Object.fromEntries(SUBJECTS.map((s) => [s.key, s]));

const STAGE = { PICKER: "picker", QUIZ: "quiz", RESULT: "result" };

export default function ChapterPracticePage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState(STAGE.PICKER);
  const [activeSubject, setActiveSubject] = useState("polity");
  const [chapters, setChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(true);
  const [starting, setStarting] = useState(null); // chapter key being started

  // Topic search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null); // {total_mcqs, chapters, ready, usable}
  const [searching, setSearching] = useState(false);

  // Quiz state
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({}); // mcq_id → chosen_index
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // ─── Fetch chapters when subject changes ───
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingChapters(true);
      try {
        const r = await managedAxios.get(
          `${API_BASE}/chapter-practice/chapters?subject=${activeSubject}`,
        );
        if (alive) setChapters(r.data.chapters || []);
      } catch (e) {
        if (alive) toast.error("Failed to load chapters");
      } finally {
        if (alive) setLoadingChapters(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [activeSubject]);

  // ─── Debounced topic search ───
  // ─── Debounced topic search (ref-based to avoid set-state-in-effect lint) ───
  const searchTimerRef = React.useRef(null);
  const aliveRef = React.useRef({ generation: 0 });

  const runSearch = (rawQuery) => {
    const q = (rawQuery || "").trim();
    if (q.length < 2) {
      // Render-guard in JSX prevents stale results from showing
      return;
    }
    setSearching(true);
    aliveRef.current.generation += 1;
    const myGen = aliveRef.current.generation;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const r = await managedAxios.get(
          `${API_BASE}/chapter-practice/search?q=${encodeURIComponent(q)}`,
        );
        if (aliveRef.current.generation === myGen) setSearchResult(r.data);
      } catch (e) {
        if (aliveRef.current.generation === myGen) {
          setSearchResult({ total_mcqs: 0, chapters: [], ready: false, usable: false });
        }
      } finally {
        if (aliveRef.current.generation === myGen) setSearching(false);
      }
    }, 280);
  };

  // ─── Handle paywall / errors from /start ───
  const handleStartError = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 402 || (detail && typeof detail === "object" && detail.paywall)) {
      toast.error(detail?.message || "You've reached your free daily practice limit. Upgrade to Pro for unlimited practice.", {
        action: { label: "Upgrade to Pro", onClick: () => navigate("/pricing") },
        duration: 8000,
      });
      return;
    }
    toast.error((typeof detail === "string" && detail) || fallback);
  };

  // ─── Start a session ───
  const handleStart = async (chapter) => {
    setStarting(chapter);
    try {
      const r = await managedAxios.post(`${API_BASE}/chapter-practice/start`, {
        subject: activeSubject,
        chapter,
        mode: "mixed",
        size: 20,
      });
      setSession(r.data);
      setAnswers({});
      setCurrentIdx(0);
      setStage(STAGE.QUIZ);
    } catch (e) {
      handleStartError(e, "Could not start session");
    } finally {
      setStarting(null);
    }
  };

  // ─── Start a session by topic search ───
  const handleStartByTopic = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    setStarting(`topic:${q}`);
    try {
      const r = await managedAxios.post(`${API_BASE}/chapter-practice/start`, {
        topic_query: q,
        mode: "mixed",
        size: 20,
      });
      setSession(r.data);
      setAnswers({});
      setCurrentIdx(0);
      setStage(STAGE.QUIZ);
    } catch (e) {
      handleStartError(e, "Could not start topic session");
    } finally {
      setStarting(null);
    }
  };

  // ─── Submit answers ───
  const handleSubmit = async () => {
    if (!session) return;
    setSubmitting(true);
    try {
      const payload = {
        session_id: session.session_id,
        answers: session.mcqs.map((m) => ({
          mcq_id: m.mcq_id,
          chosen_index: answers[m.mcq_id] ?? -1,
        })),
      };
      const r = await managedAxios.post(
        `${API_BASE}/chapter-practice/submit`,
        payload,
      );
      setResult(r.data);
      setStage(STAGE.RESULT);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSession(null);
    setResult(null);
    setAnswers({});
    setStage(STAGE.PICKER);
  };

  // ────────────────────── RENDER ──────────────────────
  if (stage === STAGE.QUIZ && session)
    return (
      <QuizRunner
        session={session}
        answers={answers}
        currentIdx={currentIdx}
        onSetAnswer={(mcqId, idx) =>
          setAnswers((p) => ({ ...p, [mcqId]: idx }))
        }
        onNavigate={setCurrentIdx}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    );

  if (stage === STAGE.RESULT && result)
    return <ResultPanel result={result} sessionMcqs={session?.mcqs || []} onRetry={handleRetry} />;

  // Picker stage
  const activeSubjMeta = SUBJECT_MAP[activeSubject] || SUBJECTS[0];
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      {/* ── HERO ──────────────────────────────────────────────── */}
      {/* NOTE: no `overflow-hidden` here — would clip the floating search-result dropdown.
          The two decorative bg layers below are absolute inset-0, so they self-clip. */}
      <div className="relative z-10 bg-gradient-to-br from-indigo-950 via-slate-900 to-blue-950 text-white">
        {/* Decorative grid + glow */}
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(99,102,241,0.4), transparent 40%), radial-gradient(circle at 80% 60%, rgba(56,189,248,0.3), transparent 40%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
          <button
            onClick={() => navigate("/prelims")}
            className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs text-white/80 backdrop-blur-sm transition hover:bg-white/20 hover:text-white"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Prelims Hub
          </button>

          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-indigo-300/90">
              8 subjects · unlimited subtopics · zero filler
            </p>
            <h1 className="mt-3 font-serif text-5xl tracking-tight text-white sm:text-6xl">
              Practice Hub
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/75">
              Practice 20 MCQs on <span className="text-white">any chapter</span> — or drill into{" "}
              <span className="text-white">any subtopic</span> across every subject. UPSC negative marking · instant explanations · current-affairs angles.
            </p>
          </div>

          {/* Topic search — overlapping pill style */}
          <div className="relative mt-8" data-testid="topic-search-block">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  runSearch(e.target.value);
                }}
                placeholder="Practice MCQs on any topic or subtopic — e.g. 'Right to Privacy', 'Basic Structure', 'Quit India'"
                className="h-12 rounded-full border-0 bg-white/95 pl-11 pr-12 text-sm text-stone-900 shadow-xl shadow-indigo-950/40 placeholder:text-stone-500 focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-0"
                data-testid="topic-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-stone-400 transition hover:bg-stone-100 hover:text-stone-700"
                  data-testid="topic-search-clear"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Example subtopic chips */}
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="topic-example-chips">
              <span className="text-[11px] uppercase tracking-wider text-white/45">Try:</span>
              {["Right to Privacy", "Basic Structure", "Anti-Defection", "Fundamental Duties", "Preamble"].map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => { setSearchQuery(chip); runSearch(chip); }}
                  data-testid={`topic-chip-${chip.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/85 backdrop-blur-sm transition hover:border-indigo-300/50 hover:bg-white/20 hover:text-white"
                >
                  {chip}
                </button>
              ))}
            </div>

            {/* Live search result — floats below */}
            {searchQuery.trim().length >= 2 && (
              <div
                className="absolute left-0 right-0 top-full z-20 mt-2 rounded-2xl border border-stone-200 bg-white p-4 text-sm shadow-2xl shadow-indigo-950/30"
                data-testid="topic-search-result"
              >
                {searching ? (
                  <div className="flex items-center gap-2 text-stone-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
                  </div>
                ) : !searchResult ? null : searchResult.total_mcqs === 0 ? (
                  <p className="text-stone-500">
                    No MCQs found for <span className="font-medium text-stone-700">&ldquo;{searchResult.query}&rdquo;</span>.
                    Try a broader term or pick a chapter below.
                  </p>
                ) : (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-stone-700">
                      <span className="font-serif text-2xl text-indigo-700">
                        {searchResult.total_mcqs}
                      </span>{" "}
                      <span className="font-medium">MCQ{searchResult.total_mcqs !== 1 ? "s" : ""}</span> matched
                      {searchResult.chapters?.length > 0 && (
                        <span className="ml-1 text-stone-500">
                          · across{" "}
                          {searchResult.chapters
                            .slice(0, 2)
                            .map((c) => c.chapter)
                            .join(", ")}
                          {searchResult.chapters.length > 2 && ` +${searchResult.chapters.length - 2}`}
                        </span>
                      )}
                      {!searchResult.ready && searchResult.usable && (
                        <span className="ml-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                          Will pad with closest chapter
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={handleStartByTopic}
                      disabled={!searchResult.usable || starting !== null}
                      className="rounded-full bg-indigo-600 px-5 text-white shadow-md transition hover:bg-indigo-700 disabled:opacity-40"
                      data-testid="start-topic-session"
                    >
                      {starting === `topic:${searchQuery.trim()}` ? (
                        <>
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Starting…
                        </>
                      ) : (
                        <>
                          Start session <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── BODY ──────────────────────────────────────────────── */}
      <div className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6">
        {/* Subject pill row */}
        <div className="mb-6 flex flex-wrap gap-2" data-testid="subject-tabs">
          {SUBJECTS.map((s) => {
            const Icon = s.icon;
            const active = s.key === activeSubject;
            return (
              <button
                key={s.key}
                onClick={() => setActiveSubject(s.key)}
                data-testid={`subject-tab-${s.key}`}
                className={`group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${
                  active
                    ? "text-white shadow-md ring-2 ring-offset-2 ring-offset-stone-50"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-900"
                }`}
                style={
                  active
                    ? {
                        backgroundColor: s.hex,
                        boxShadow: `0 4px 14px ${s.hex}40`,
                        ringColor: s.hex,
                      }
                    : undefined
                }
              >
                <Icon className={`h-4 w-4 ${active ? "" : "text-stone-500 group-hover:text-stone-700"}`} />
                <span className="font-medium">{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Active subject heading */}
        <div className="mb-5 flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-xl text-white shadow"
            style={{ backgroundColor: activeSubjMeta.hex }}
          >
            <activeSubjMeta.icon className="h-4.5 w-4.5" />
          </div>
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-stone-900">
              {activeSubjMeta.label}
            </h2>
            <p className="text-xs text-stone-500">
              {chapters.length} chapter{chapters.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Chapter grid */}
        {loadingChapters ? (
          <div className="flex h-64 items-center justify-center" data-testid="chapters-loading">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
          </div>
        ) : chapters.length === 0 ? (
          <p className="py-12 text-center text-stone-500" data-testid="chapters-empty">
            No chapters in this subject yet.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="chapters-grid">
            {chapters.map((ch) => (
              <ChapterCard
                key={`${ch.subject}-${ch.chapter}`}
                chapter={ch}
                subject={activeSubjMeta}
                starting={starting === ch.chapter}
                disabled={starting !== null || ch.static_count + ch.ca_count < 5}
                onStart={() => handleStart(ch.chapter)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
function ChapterCard({ chapter, subject, starting, disabled, onStart }) {
  const ca = chapter.ca_count;
  const hasCA = ca > 0;

  return (
    <Card
      className="group relative overflow-hidden border-stone-200/70 bg-white p-0 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-xl"
      data-testid={`chapter-card-${chapter.chapter.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {/* Color stripe on left, themed to subject */}
      <div
        className="absolute inset-y-0 left-0 w-1 transition-all group-hover:w-1.5"
        style={{ backgroundColor: subject.hex }}
      />
      <div className="px-5 pb-4 pt-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p
              className="text-[10px] uppercase tracking-[0.2em]"
              style={{ color: subject.hex }}
            >
              {subject.label}
            </p>
            <h3 className="mt-0.5 text-base font-semibold leading-snug text-stone-900">
              {chapter.chapter}
            </h3>
          </div>
          {hasCA && (
            <div
              className="flex flex-shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200"
              title="Fresh current-affairs MCQs included"
            >
              <Flame className="h-3 w-3" />
              {ca} fresh
            </div>
          )}
        </div>

        <Button
          onClick={onStart}
          disabled={disabled}
          className="w-full rounded-lg text-white shadow-sm transition hover:shadow-md disabled:opacity-40"
          style={{ backgroundColor: subject.hex }}
          data-testid={`start-practice-${chapter.chapter.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {starting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…
            </>
          ) : (
            <>
              Start 20-MCQ session <ArrowRight className="ml-1.5 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════
function QuizRunner({
  session,
  answers,
  currentIdx,
  onSetAnswer,
  onNavigate,
  onSubmit,
  submitting,
}) {
  const total = session.mcqs.length;
  const mcq = session.mcqs[currentIdx];
  const attempted = Object.keys(answers).length;
  const isCA = mcq.source === "ca";

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-5 flex items-center justify-between" data-testid="quiz-header">
          <div>
            <p className="text-xs uppercase tracking-wider text-stone-500">
              {session.subject} · {session.chapter}
            </p>
            <p className="mt-0.5 text-sm font-medium text-stone-700">
              {currentIdx + 1} / {total} · {attempted} answered
            </p>
          </div>
          <Button
            onClick={onSubmit}
            disabled={submitting || attempted === 0}
            variant="default"
            className="bg-stone-900 text-white hover:bg-stone-800"
            data-testid="submit-quiz-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scoring…
              </>
            ) : (
              <>
                <Trophy className="mr-1.5 h-4 w-4" />
                Submit
              </>
            )}
          </Button>
        </header>

        {/* Progress bar */}
        <div className="mb-5 h-1 w-full overflow-hidden rounded-full bg-stone-200">
          <div
            className="h-full bg-stone-900 transition-all"
            style={{ width: `${((currentIdx + 1) / total) * 100}%` }}
          />
        </div>

        {/* Question card — keyed by mcq_id so it fully remounts on navigation.
            Prevents Google Translate from leaving stale translated option text
            when React reuses option DOM nodes across questions. */}
        <Card key={mcq.mcq_id} className="border-stone-200" data-testid="question-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Badge
                variant="outline"
                className={
                  isCA
                    ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                    : "border-stone-300 bg-stone-100 text-stone-700"
                }
                data-testid="mcq-source-badge"
              >
                {isCA ? (
                  <>
                    <Newspaper className="mr-1 h-3 w-3" /> Current Affairs ·{" "}
                    {mcq.ca_date || "recent"}
                  </>
                ) : (
                  <>
                    <BookOpen className="mr-1 h-3 w-3" /> Static
                  </>
                )}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {mcq.difficulty}
              </Badge>
            </div>
            <CardTitle className="mt-4 text-base font-normal text-stone-900 leading-relaxed">
              {mcq.question_text}
            </CardTitle>
            {isCA && mcq.source_title && (
              <p className="mt-2 text-xs italic text-stone-500">
                From: {mcq.source_title}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-2.5">
            {mcq.options.map((opt, i) => {
              const chosen = answers[mcq.mcq_id] === i;
              return (
                <button
                  key={`${mcq.mcq_id}-${i}`}
                  onClick={() => onSetAnswer(mcq.mcq_id, i)}
                  data-testid={`option-${i}`}
                  className={`w-full rounded-md border px-4 py-3 text-left text-sm leading-relaxed transition ${
                    chosen
                      ? "border-stone-900 bg-stone-900 text-white"
                      : "border-stone-200 bg-white text-stone-800 hover:border-stone-400"
                  }`}
                >
                  <span className="mr-2 font-medium">
                    {String.fromCharCode(65 + i)}.
                  </span>
                  {opt}
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => onNavigate(Math.max(0, currentIdx - 1))}
            disabled={currentIdx === 0}
            data-testid="prev-mcq-btn"
          >
            ← Previous
          </Button>
          <Button
            variant="ghost"
            onClick={() => onNavigate(Math.min(total - 1, currentIdx + 1))}
            disabled={currentIdx === total - 1}
            data-testid="next-mcq-btn"
          >
            Next →
          </Button>
        </div>

        {/* Question palette */}
        <div className="mt-6">
          <p className="mb-2 text-xs uppercase tracking-wider text-stone-500">
            Jump to question
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="question-palette">
            {session.mcqs.map((m, i) => {
              const answered = m.mcq_id in answers;
              return (
                <button
                  key={m.mcq_id}
                  onClick={() => onNavigate(i)}
                  className={`h-8 w-8 rounded text-xs font-medium ${
                    i === currentIdx
                      ? "bg-stone-900 text-white ring-2 ring-stone-300"
                      : answered
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                  }`}
                  data-testid={`palette-${i}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
function ResultPanel({ result, sessionMcqs, onRetry }) {
  const s = result.score;
  const pct = s.attempted ? (100 * s.correct) / s.attempted : 0;
  const wrong = s.attempted - s.correct;

  const grade =
    pct >= 75 ? "Excellent" : pct >= 50 ? "Good" : pct >= 30 ? "Practice more" : "Needs work";
  const gradeTheme =
    pct >= 75
      ? { ring: "ring-emerald-200", bg: "from-emerald-500 to-teal-600", text: "text-emerald-50", chip: "bg-emerald-100 text-emerald-800" }
      : pct >= 50
      ? { ring: "ring-blue-200", bg: "from-indigo-600 to-blue-700", text: "text-indigo-50", chip: "bg-indigo-100 text-indigo-800" }
      : pct >= 30
      ? { ring: "ring-amber-200", bg: "from-amber-500 to-orange-600", text: "text-amber-50", chip: "bg-amber-100 text-amber-800" }
      : { ring: "ring-rose-200", bg: "from-rose-500 to-pink-600", text: "text-rose-50", chip: "bg-rose-100 text-rose-800" };

  // Build mcq_id → mcq lookup (questions/options are NOT on results array)
  const mcqMap = Object.fromEntries(sessionMcqs.map((m) => [m.mcq_id, m]));

  // Filter helpers
  const [reviewFilter, setReviewFilter] = React.useState("all"); // all | wrong | skipped | ca

  const filtered = result.results.filter((r) => {
    if (reviewFilter === "all") return true;
    if (reviewFilter === "wrong") return !r.is_skipped && !r.is_correct;
    if (reviewFilter === "skipped") return r.is_skipped;
    if (reviewFilter === "ca") return mcqMap[r.mcq_id]?.source === "ca";
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-stone-50">
      {/* ── Hero header ───────────────────────────────────────── */}
      <div className={`bg-gradient-to-br ${gradeTheme.bg} text-white`}>
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
          <p className={`text-[11px] uppercase tracking-[0.2em] ${gradeTheme.text} opacity-80`}>
            {result.subject} · {result.chapter}
          </p>
          <h1 className="mt-3 font-serif text-5xl tracking-tight sm:text-6xl" data-testid="result-grade">
            {grade}
          </h1>
          <p className="mt-2 max-w-xl text-sm text-white/85">
            {pct >= 75
              ? "You're operating at exam-ready depth on this chapter. Push to harder questions."
              : pct >= 50
              ? "Foundations are showing — sharpen edge cases & current-affairs angles."
              : pct >= 30
              ? "Clear gaps exist. Walk through every explanation below before the next session."
              : "Don't sweat the score — read every explanation. Revisit chapter notes, then re-attempt."}
          </p>

          {/* Score tiles */}
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="result-summary">
            <ScoreTile label="Correct" value={`${s.correct}/${s.total}`} accent={gradeTheme.ring} />
            <ScoreTile label="Wrong" value={wrong} accent={gradeTheme.ring} />
            <ScoreTile label="Skipped" value={s.skipped} accent={gradeTheme.ring} />
            <ScoreTile
              label="Net (UPSC)"
              value={s.score.toFixed(2)}
              accent={gradeTheme.ring}
              footnote={`−⅓ on wrong · ${pct.toFixed(0)}% acc`}
            />
          </div>
        </div>
      </div>

      {/* ── Action bar ────────────────────────────────────────── */}
      <div className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2 overflow-x-auto" data-testid="review-filters">
            {[
              { key: "all", label: `All (${result.results.length})` },
              { key: "wrong", label: `Wrong (${wrong})` },
              { key: "skipped", label: `Skipped (${s.skipped})` },
              { key: "ca", label: "Current Affairs" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setReviewFilter(f.key)}
                data-testid={`review-filter-${f.key}`}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  reviewFilter === f.key
                    ? "bg-stone-900 text-white"
                    : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button
            onClick={onRetry}
            className="ml-3 hidden bg-stone-900 text-white hover:bg-stone-800 sm:inline-flex"
            data-testid="practice-again-btn"
          >
            Practice another chapter →
          </Button>
        </div>
      </div>

      {/* ── Review list ───────────────────────────────────────── */}
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="space-y-4" data-testid="result-explanations">
          {filtered.length === 0 ? (
            <Card className="border-stone-200">
              <CardContent className="py-10 text-center text-sm text-stone-500">
                Nothing matches this filter — try another tab above.
              </CardContent>
            </Card>
          ) : (
            filtered.map((r) => {
              const mcq = mcqMap[r.mcq_id];
              const origIdx = result.results.findIndex((x) => x.mcq_id === r.mcq_id);
              return (
                <ReviewQuestion
                  key={r.mcq_id}
                  idx={origIdx}
                  result={r}
                  mcq={mcq}
                />
              );
            })
          )}
        </div>

        {/* Footer CTA (mobile + bottom on desktop) */}
        <div className="mt-8 flex justify-center">
          <Button
            onClick={onRetry}
            size="lg"
            className="bg-stone-900 text-white hover:bg-stone-800"
            data-testid="practice-again-btn-bottom"
          >
            Practice another chapter →
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Score tile (hero) ─────────────────────────────────────────
function ScoreTile({ label, value, footnote }) {
  return (
    <div className="rounded-xl bg-white/10 px-4 py-4 backdrop-blur-sm ring-1 ring-white/15">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">{label}</div>
      <div className="mt-1.5 font-serif text-3xl tracking-tight text-white sm:text-4xl">{value}</div>
      {footnote && <div className="mt-1 text-[10px] text-white/60">{footnote}</div>}
    </div>
  );
}

// ── Single review card with question + options + explanation ──
function ReviewQuestion({ idx, result, mcq }) {
  const isCA = mcq?.source === "ca";
  const verdict = result.is_skipped
    ? { tone: "skipped", chip: "bg-stone-200 text-stone-700", border: "border-l-stone-300", icon: null, label: "Skipped" }
    : result.is_correct
    ? { tone: "correct", chip: "bg-emerald-100 text-emerald-800", border: "border-l-emerald-500", icon: <CheckCircle2 className="h-3.5 w-3.5" />, label: "Correct" }
    : { tone: "wrong", chip: "bg-rose-100 text-rose-800", border: "border-l-rose-500", icon: <XCircle className="h-3.5 w-3.5" />, label: "Wrong" };

  return (
    <Card
      className={`overflow-hidden border-stone-200 border-l-4 shadow-sm ${verdict.border}`}
      data-testid={`review-card-${idx}`}
    >
      <CardContent className="space-y-4 px-5 py-5 sm:px-6">
        {/* meta row */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-stone-500">Q{idx + 1}</span>
          <Badge
            variant="outline"
            className={
              isCA
                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : "border-stone-300 bg-stone-50 text-stone-700"
            }
          >
            {isCA ? (
              <>
                <Newspaper className="mr-1 h-3 w-3" /> Current Affairs
              </>
            ) : (
              <>
                <BookOpen className="mr-1 h-3 w-3" /> Evergreen
              </>
            )}
          </Badge>
          {mcq?.difficulty && (
            <Badge variant="outline" className="text-xs capitalize">
              {mcq.difficulty}
            </Badge>
          )}
          <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${verdict.chip}`}>
            {verdict.icon}
            {verdict.label}
          </span>
        </div>

        {/* question text */}
        {mcq?.question_text && (
          <p className="whitespace-pre-line text-[15px] leading-relaxed text-stone-900">
            {mcq.question_text}
          </p>
        )}

        {/* options */}
        {Array.isArray(mcq?.options) && (
          <div className="space-y-2">
            {mcq.options.map((opt, i) => {
              const isCorrectOpt = i === result.correct_index;
              const isYourOpt = i === result.chosen_index;
              let cls = "border-stone-200 bg-white text-stone-700";
              let tag = null;
              if (isCorrectOpt) {
                cls = "border-emerald-300 bg-emerald-50 text-emerald-900";
                tag = (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" /> Correct answer
                  </span>
                );
              } else if (isYourOpt) {
                cls = "border-rose-300 bg-rose-50 text-rose-900";
                tag = (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700">
                    <XCircle className="h-3 w-3" /> Your choice
                  </span>
                );
              }
              return (
                <div
                  key={i}
                  className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm leading-relaxed ${cls}`}
                >
                  <span className="mt-0.5 font-semibold">{String.fromCharCode(65 + i)}.</span>
                  <span className="flex-1">
                    {opt}
                    {tag}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* explanation */}
        {result.explanation && (
          <div className="rounded-lg border border-stone-200 bg-stone-50/70 px-4 py-3">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              <Sparkles className="h-3 w-3" />
              Explanation
            </div>
            <p className="whitespace-pre-line text-sm leading-relaxed text-stone-700">
              {result.explanation}
            </p>
            {isCA && mcq?.source_title && (
              <p className="mt-2 text-[11px] italic text-stone-500">From: {mcq.source_title}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
